import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Bot, FileText, BarChart3, Shield, TrendingUp, Lightbulb } from 'lucide-react';
import AIService from '../../services/aiService';
import ReactMarkdown from 'react-markdown';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AskCAM360 = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);

  // Quick action categories
  const quickActions = [
    {
      category: 'Contracts',
      icon: <FileText size={20} />,
      color: '#3b82f6',
      actions: [
        { text: 'Show contract renewals', query: 'Show me my contract renewals' },
        { text: 'Contract analysis', query: 'Help me with contract analysis' },
        { text: 'Risk assessment', query: 'Analyze contract risks' },
        { text: 'Compliance check', query: 'Check contract compliance' }
      ]
    },
    {
      category: 'Assets',
      icon: <BarChart3 size={20} />,
      color: '#10b981',
      actions: [
        { text: 'Maintenance alerts', query: 'What assets need maintenance?' },
        { text: 'Asset valuation', query: 'Show asset valuations' },
        { text: 'Depreciation report', query: 'Generate depreciation report' },
        { text: 'Asset utilization', query: 'Show asset utilization rates' }
      ]
    },
    {
      category: 'Analytics',
      icon: <TrendingUp size={20} />,
      color: '#f59e0b',
      actions: [
        { text: 'Performance metrics', query: 'Show performance metrics' },
        { text: 'Budget analysis', query: 'Analyze budget vs actual' },
        { text: 'ROI calculation', query: 'Calculate ROI for projects' },
        { text: 'Trend analysis', query: 'Show trend analysis' }
      ]
    },
    {
      category: 'Compliance',
      icon: <Shield size={20} />,
      color: '#ef4444',
      actions: [
        { text: 'Compliance report', query: 'Generate a compliance report' },
        { text: 'Audit checklist', query: 'Show audit checklist' },
        { text: 'Regulatory updates', query: 'Check regulatory updates' },
        { text: 'Risk assessment', query: 'Perform risk assessment' }
      ]
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const callGeminiAPI = async (userMessage) => {
    try {
      const data = await AIService.chatWithGemini(userMessage);
      return data.response || "I'm sorry, I couldn't process your request at the moment. Please try again.";
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      
      if (error.message.includes('404')) {
        return "The AI backend server isn't running. Please start the backend server and try again. For now, here's a helpful response: " + 
               "Asset depreciation can be tracked using methods like straight-line, declining balance, or units of production. " +
               "For tax purposes, consider MACRS (Modified Accelerated Cost Recovery System) which provides faster depreciation " +
               "in early years. Keep detailed records of asset costs, useful life, and depreciation calculations for tax compliance.";
      }
      
      return "I'm experiencing technical difficulties. Please try again later or contact support.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      content: userMessage,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);
    setIsLoading(true);

    try {
      const aiResponse = await callGeminiAPI(userMessage);
      
      const aiMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: aiResponse,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: "I'm sorry, I encountered an error while processing your request. Please try again.",
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = async (query) => {
    setShowWelcome(false);
    
    const userMessage = query;
    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      content: userMessage,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, newMessage]);
    setIsTyping(true);
    setIsLoading(true);

    try {
      const aiResponse = await callGeminiAPI(userMessage);
      
      const aiMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: aiResponse,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: "I'm sorry, I encountered an error while processing your request. Please try again.",
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setShowWelcome(true);
    setInputValue('');
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Ask CAM360"
        description="Get instant answers to your questions about contracts, assets, and business processes."
      />
      <div className="page-container">
        <div 
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {messages.length > 0 && (
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '16px', 
              justifyContent: 'flex-end',
              width: '100%'
            }}>
              <button 
                className="btn-modern btn-modern-outline"
                onClick={startNewChat}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontFamily: 'var(--font-family)'
                }}
              >
                <MessageCircle size={16} />
                New Chat
              </button>
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '24px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Left Chat Area - 65% */}
            <div style={{ flex: '0 0 65%', display: 'flex', flexDirection: 'column', minWidth: 0, marginLeft: '-15px' }}>
              <div className="asset-landing-card" style={{ 
                height: 'calc(100vh - 280px)',
                maxHeight: '650px',
                display: 'flex', 
                flexDirection: 'column', 
                width: '100%', 
                boxSizing: 'border-box' 
              }}>
                {/* Top Accent Line */}
                <div
                  style={{
                    height: '4px',
                    background: 'var(--primary-color)',
                    width: '100%'
                  }}
                />
                {showWelcome ? (
                  /* Welcome Message */
                  <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ flex: 1, padding: '16px 24px', maxWidth: '500px', margin: '0 auto', width: '100%' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: '#D6EFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px'
                    }}>
                      <Bot size={24} style={{ color: '#2D7DD2' }} />
                    </div>
                    
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#1f2937', 
                      marginBottom: '8px',
                      fontFamily: 'var(--font-family)'
                    }}>
                      Welcome to CAM360 AI Assistant
                    </h3>
                    
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#6b7280', 
                      marginBottom: '20px',
                      maxWidth: '100%',
                      lineHeight: '1.5',
                      fontFamily: 'var(--font-family)'
                    }}>
                      I'm your intelligent assistant for Contract LifeCycle Management. I can help you with analysis, 
                      reports, compliance checks, and much more. Choose a quick action on the right or ask me anything!
                    </p>

                    {/* Input Area */}
                    <div style={{ width: '100%', maxWidth: '100%' }}>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1" style={{ marginRight: '12px' }}>
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask me anything about your contracts, assets, or business processes..."
                            disabled={isLoading}
                            className="form-control"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '1px solid #d1d5db',
                              borderRadius: '0',
                              fontSize: '14px',
                              outline: 'none',
                              transition: 'all 0.2s',
                              background: 'white',
                              fontFamily: 'var(--font-family)'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = 'var(--primary-color)';
                              e.target.style.boxShadow = '0 0 0 3px rgba(70, 120, 190, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#d1d5db';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                        
                        <button 
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || isLoading}
                          className="btn-modern btn-modern-primary"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '0',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (inputValue.trim() && !isLoading) ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s',
                            fontFamily: 'var(--font-family)',
                            minWidth: '40px',
                            padding: '0',
                            opacity: (!inputValue.trim() || isLoading) ? 0.5 : 1
                          }}
                        >
                          <Send size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Chat Interface */
                  <>
                    {/* Chat Messages */}
                    <div 
                      className="card-body" 
                      style={{ 
                        flex: 1, 
                        overflowY: 'auto', 
                        padding: '20px',
                        background: '#f8fafc'
                      }}
                    >
                      <div className="chat-messages">
                        {messages.length === 0 && (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No messages yet. Try clicking a quick action or typing a message.
                          </div>
                        )}
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`message ${message.type === 'user' ? 'user-message' : 'ai-message'}`}
                            style={{
                              marginBottom: '16px',
                              display: 'flex',
                              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <div
                              style={{
                                maxWidth: '70%',
                                padding: '12px 16px',
                                borderRadius: message.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                background: message.type === 'user' ? 'var(--primary-color)' : 'white',
                                color: message.type === 'user' ? 'white' : '#1f2937',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                position: 'relative'
                              }}
                            >
                              {message.type === 'ai' && (
                                <div 
                                  style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    left: '8px',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: 'var(--primary-color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Bot size={12} style={{ color: 'white' }} />
                                </div>
                              )}
                              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                {message.type === 'ai' ? (
                                  <ReactMarkdown 
                                    components={{
                                      h1: ({children}) => <h1 style={{fontSize: '18px', fontWeight: 'bold', margin: '8px 0 4px 0', color: '#1f2937'}}>{children}</h1>,
                                      h2: ({children}) => <h2 style={{fontSize: '16px', fontWeight: 'bold', margin: '6px 0 3px 0', color: '#1f2937'}}>{children}</h2>,
                                      h3: ({children}) => <h3 style={{fontSize: '15px', fontWeight: 'bold', margin: '4px 0 2px 0', color: '#1f2937'}}>{children}</h3>,
                                      p: ({children}) => <p style={{margin: '4px 0', lineHeight: '1.6'}}>{children}</p>,
                                      ul: ({children}) => <ul style={{margin: '4px 0', paddingLeft: '16px'}}>{children}</ul>,
                                      ol: ({children}) => <ol style={{margin: '4px 0', paddingLeft: '16px'}}>{children}</ol>,
                                      li: ({children}) => <li style={{margin: '2px 0', lineHeight: '1.5'}}>{children}</li>,
                                      strong: ({children}) => <strong style={{fontWeight: 'bold', color: '#1f2937'}}>{children}</strong>,
                                      em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                                      code: ({children}) => <code style={{backgroundColor: '#f3f4f6', padding: '2px 4px', borderRadius: '3px', fontSize: '13px', fontFamily: 'monospace'}}>{children}</code>,
                                      blockquote: ({children}) => <blockquote style={{borderLeft: '3px solid #e5e7eb', paddingLeft: '12px', margin: '8px 0', fontStyle: 'italic', color: '#6b7280'}}>{children}</blockquote>
                                    }}
                                  >
                                    {message.content}
                                  </ReactMarkdown>
                                ) : (
                                  message.content
                                )}
                              </div>
                              <div 
                                style={{ 
                                  fontSize: '11px', 
                                  opacity: 0.7, 
                                  marginTop: '4px',
                                  textAlign: message.type === 'user' ? 'right' : 'left'
                                }}
                              >
                                {message.timestamp}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {isTyping && (
                          <div className="message ai-message" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <div
                              style={{
                                padding: '12px 16px',
                                borderRadius: '18px 18px 18px 4px',
                                background: 'white',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '200px'
                              }}
                            >
                              <div 
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  left: '8px',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: 'var(--primary-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Bot size={12} style={{ color: 'white' }} />
                              </div>
                              <div className="typing-indicator" style={{ marginLeft: '8px' }}>
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                              <span style={{ marginLeft: '12px', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                                AI is thinking...
                              </span>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Input Area */}
                    <div 
                      style={{ 
                        padding: '16px 20px', 
                        borderTop: '1px solid #e5e7eb',
                        background: 'white',
                        position: 'relative'
                      }}
                    >
                      {isLoading && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            borderRadius: '0 0 8px 8px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="spinner" style={{
                              width: '20px',
                              height: '20px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>
                              Processing your request...
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1" style={{ marginRight: '12px' }}>
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask me anything about your contracts, assets, or business processes..."
                            disabled={isLoading}
                            className="form-control"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: isLoading ? '1px solid var(--primary-color)' : '1px solid #d1d5db',
                              borderRadius: '0',
                              fontSize: '14px',
                              outline: 'none',
                              transition: 'all 0.2s',
                              background: isLoading ? '#f8fafc' : 'white',
                              fontFamily: 'var(--font-family)'
                            }}
                            onFocus={(e) => !isLoading && (e.target.style.borderColor = 'var(--primary-color)') && (e.target.style.boxShadow = '0 0 0 3px rgba(70, 120, 190, 0.1)')}
                            onBlur={(e) => !isLoading && (e.target.style.borderColor = '#d1d5db') && (e.target.style.boxShadow = 'none')}
                          />
                        </div>
                        
                        <button 
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim() || isLoading}
                          className="btn-modern btn-modern-primary"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '0',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (inputValue.trim() && !isLoading) ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s',
                            position: 'relative',
                            fontFamily: 'var(--font-family)',
                            minWidth: '40px',
                            padding: '0',
                            opacity: (!inputValue.trim() || isLoading) ? 0.5 : 1
                          }}
                        >
                          {isLoading ? (
                            <div className="spinner" style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid transparent',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Quick Actions - 35% */}
            <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div className="asset-landing-card" style={{ 
                height: 'calc(100vh - 280px)',
                maxHeight: '650px',
                position: 'sticky', 
                top: '0', 
                width: '100%', 
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Top Accent Line */}
                <div
                  style={{
                    height: '4px',
                    background: 'var(--primary-color)',
                    width: '100%'
                  }}
                />
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: '#D6EFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}
                    >
                      <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                        <Lightbulb size={16} />
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h4 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1f2937',
                        margin: '0',
                        fontFamily: 'var(--font-family)'
                      }}>
                        Quick Actions
                      </h4>
                    </div>
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1, paddingTop: '4px' }}>
                    {quickActions.map((category, index) => (
                      <div key={index} style={{ marginBottom: '20px' }}>
                        <div className="d-flex align-items-center mb-2">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              background: '#D6EFFF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '10px',
                              flexShrink: 0
                            }}
                          >
                            <div style={{ color: '#2D7DD2' }}>
                              {category.icon}
                            </div>
                          </div>
                          <h6 style={{ 
                            color: '#1f2937', 
                            fontWeight: '600', 
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                            margin: '0',
                            padding: '0',
                            fontFamily: 'var(--font-family)'
                          }}>
                            {category.category}
                          </h6>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                          {category.actions.map((action, actionIndex) => (
                            <button
                              key={actionIndex}
                              className="btn-modern btn-modern-outline"
                              onClick={() => handleQuickAction(action.query)}
                              style={{
                                textAlign: 'left',
                                padding: '8px 12px',
                                borderRadius: '0',
                                border: '1px solid #e5e7eb',
                                background: 'white',
                                color: '#1f2937',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minHeight: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                fontFamily: 'var(--font-family)',
                                width: '100%',
                                justifyContent: 'flex-start'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--primary-color)';
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.color = 'var(--primary-color)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#1f2937';
                              }}
                            >
                              {action.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .typing-indicator {
          display: flex;
          align-items: center;
        }
        
        .typing-indicator span {
          height: 8px;
          width: 8px;
          background: #3b82f6;
          border-radius: 50%;
          display: inline-block;
          margin-right: 4px;
          animation: typing 1.4s infinite ease-in-out;
        }
        
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        `
      }} />
    </>
  );
};

export default AskCAM360;
